import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Users, ListChecks, Calendar as CalendarIcon, User } from 'lucide-react';
import { ProjectStatusPill } from './ProjectStatusPill';
import { ProjectProgressBar } from './ProjectProgressBar';
import { ProjectStatus, formatDate } from './projectUtils';

export interface ProjectCardData {
  id: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  startDate?: string | null;
  plannedEndDate?: string | null;
  memberCount?: number | null;
  taskCount?: number | null;
  doneTaskCount?: number | null;
  leader?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

interface ProjectCardProps {
  project: ProjectCardData;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const navigate = useNavigate();

  const leaderName = project.leader
    ? `${project.leader.firstName} ${project.leader.lastName}`
    : 'Brak lidera';

  return (
    <Card
      className="cursor-pointer hover:shadow-md hover:border-primary/40 transition-all"
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      <CardContent className="p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <h3
            className="font-semibold text-base leading-tight line-clamp-2 flex-1"
            title={project.name}
          >
            {project.name}
          </h3>
          <ProjectStatusPill status={project.status} />
        </div>

        {project.description && (
          <p
            className="text-sm text-muted-foreground line-clamp-2"
            title={project.description}
          >
            {project.description}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1">
            <User className="w-3.5 h-3.5" />
            {leaderName}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {project.memberCount ?? 0} czł.
          </span>
          <span className="flex items-center gap-1">
            <ListChecks className="w-3.5 h-3.5" />
            {project.taskCount ?? 0} zad.
          </span>
        </div>

        {(project.startDate || project.plannedEndDate) && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>
              {formatDate(project.startDate)} – {formatDate(project.plannedEndDate)}
            </span>
          </div>
        )}

        <ProjectProgressBar
          taskCount={project.taskCount ?? 0}
          doneTaskCount={project.doneTaskCount ?? 0}
          className="mt-1"
        />
      </CardContent>
    </Card>
  );
};
